import React, { useState } from 'react';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';
import { suggestTemplate, SUMMER_PROGRAM_TEMPLATES } from './templates';

const NewEssayForm = ({ onCreated, onCancel }) => {
  const { programs, addEssayDraft, addEssayDraftBatch } = useCollegePrepContext();

  // Prompts: array of { text, wordLimit }
  const [prompts, setPrompts] = useState([{ text: '', wordLimit: '' }]);
  const [programId, setProgramId] = useState('');
  const [programUrl, setProgramUrl] = useState('');
  const [contextNotes, setContextNotes] = useState('');
  const [showContextDump, setShowContextDump] = useState(false);
  const [mode, setMode] = useState('guided-response');

  const hasValidPrompt = prompts.some(p => p.text.trim().length > 0);

  // Determine max word limit for mode suggestion
  const maxWordLimit = Math.max(
    ...prompts.map(p => p.wordLimit ? parseInt(p.wordLimit, 10) : 0),
    0
  );
  const getSuggestedMode = () => {
    if (maxWordLimit > 0 && maxWordLimit <= 300) return 'guided-response';
    if (maxWordLimit >= 400) return 'guided-essay';
    return null;
  };
  const suggestedMode = getSuggestedMode();

  const handleAddPrompt = () => {
    setPrompts(prev => [...prev, { text: '', wordLimit: '' }]);
  };

  const handleRemovePrompt = (index) => {
    if (prompts.length <= 1) return;
    setPrompts(prev => prev.filter((_, i) => i !== index));
  };

  const handlePromptChange = (index, field, value) => {
    setPrompts(prev => prev.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasValidPrompt) return;

    const validPrompts = prompts.filter(p => p.text.trim());
    const sharedFields = {
      programId: programId || null,
      programUrl: programUrl.trim(),
      contextNotes: contextNotes.trim(),
      mode
    };

    if (validPrompts.length === 1) {
      // Single question — no group needed
      const p = validPrompts[0];
      const suggestedTemplateId = mode === 'guided-essay' ? suggestTemplate(p.text) : null;
      const draft = addEssayDraft({
        promptText: p.text.trim(),
        wordLimit: p.wordLimit ? parseInt(p.wordLimit, 10) : null,
        templateId: suggestedTemplateId,
        ...sharedFields
      });
      onCreated({ ...draft, mode });
    } else {
      // Multiple questions — create as a batch with shared group ID
      const draftsData = validPrompts.map(p => ({
        promptText: p.text.trim(),
        wordLimit: p.wordLimit ? parseInt(p.wordLimit, 10) : null,
        templateId: mode === 'guided-essay' ? suggestTemplate(p.text) : null,
        ...sharedFields
      }));
      const drafts = addEssayDraftBatch(draftsData);
      // Open the first one
      onCreated({ ...drafts[0], mode });
    }
  };

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900">Add Application Questions</h2>
        <button onClick={onCancel} className="text-sm text-stone-500 hover:text-stone-700">
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Program link + URL */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Program
            </label>
            <select
              value={programId}
              onChange={(e) => setProgramId(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white"
            >
              <option value="">Select a program...</option>
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Program Website
            </label>
            <input
              type="url"
              value={programUrl}
              onChange={(e) => setProgramUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
            />
            <p className="text-xs text-stone-400 mt-0.5">For your reference</p>
          </div>
        </div>

        {/* Questions */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Essay / Short Answer Questions <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {prompts.map((prompt, index) => (
              <div key={index} className="border border-stone-200 rounded-lg p-3 bg-white">
                <div className="flex items-start gap-2">
                  {prompts.length > 1 && (
                    <span className="text-xs font-semibold text-violet-600 bg-violet-50 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-1">
                      {index + 1}
                    </span>
                  )}
                  <div className="flex-1">
                    <textarea
                      value={prompt.text}
                      onChange={(e) => handlePromptChange(index, 'text', e.target.value)}
                      placeholder={index === 0 ? "Paste the question from the application..." : "Paste another question..."}
                      rows={2}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none"
                      autoFocus={index === 0}
                    />
                    <div className="flex items-center gap-3 mt-1.5">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-stone-500">Word limit:</label>
                        <input
                          type="number"
                          value={prompt.wordLimit}
                          onChange={(e) => handlePromptChange(index, 'wordLimit', e.target.value)}
                          placeholder="e.g. 250"
                          min={50}
                          max={2000}
                          className="w-20 px-2 py-1 border border-stone-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                        />
                      </div>
                      {prompts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePrompt(index)}
                          className="text-xs text-stone-400 hover:text-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleAddPrompt}
            className="mt-2 text-sm text-violet-600 hover:text-violet-800 font-medium"
          >
            + Add Another Question
          </button>
        </div>

        {/* Context dump */}
        <div>
          {!showContextDump ? (
            <button
              type="button"
              onClick={() => setShowContextDump(true)}
              className="text-sm text-violet-600 hover:text-violet-800 font-medium"
            >
              + Add notes, experiences, or stories to draw from (optional)
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700">
                  Your Notes & Experiences
                </label>
                <button
                  type="button"
                  onClick={() => setShowContextDump(false)}
                  className="text-xs text-stone-400 hover:text-stone-600"
                >
                  Hide
                </button>
              </div>
              <textarea
                value={contextNotes}
                onChange={(e) => setContextNotes(e.target.value)}
                placeholder={"Dump anything relevant here — projects, volunteer work, stories, things you're proud of, program details you noticed on the website, specific people or experiences.\n\nThe AI will use this as context when helping you draft your responses. No need to organize it — just get it down."}
                rows={5}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-y"
              />
              <p className="text-xs text-stone-400 mt-0.5">
                This feeds into the AI discovery process. Paste program descriptions here too.
              </p>
            </div>
          )}
        </div>

        {/* Mode selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Writing Approach
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMode('guided-response')}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                mode === 'guided-response'
                  ? 'border-violet-300 bg-violet-50'
                  : 'border-stone-200 bg-white hover:border-stone-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">💬</span>
                <span className="text-sm font-semibold text-slate-900">Guided Response</span>
              </div>
              <p className="text-xs text-stone-600">
                AI-powered discovery conversation that draws out your authentic voice, then builds a draft from your reflections.
              </p>
              {suggestedMode === 'guided-response' && (
                <span className="inline-block mt-1 text-xs text-violet-600 font-medium">Recommended for shorter prompts</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMode('guided-essay')}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                mode === 'guided-essay'
                  ? 'border-violet-300 bg-violet-50'
                  : 'border-stone-200 bg-white hover:border-stone-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📝</span>
                <span className="text-sm font-semibold text-slate-900">Guided Essay</span>
              </div>
              <p className="text-xs text-stone-600">
                Step-by-step writing with live draft preview and real-time coaching. Best for longer personal statements.
              </p>
              {suggestedMode === 'guided-essay' && (
                <span className="inline-block mt-1 text-xs text-violet-600 font-medium">Recommended for longer essays</span>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-stone-600 border border-stone-300 rounded-lg hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!hasValidPrompt}
            className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {prompts.filter(p => p.text.trim()).length > 1
              ? `Start Writing (${prompts.filter(p => p.text.trim()).length} questions)`
              : 'Start Writing'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewEssayForm;
